import { useCallback, useEffect, useState, type FormEvent } from 'react'
import { fetchOperators, fetchProducts, type Operator } from '../api/catalog'
import {
  REVENUE_BASIS_LABELS, receivablesApi, revenueRulesApi,
  type ReceivableSettings, type RevenueBasis, type RevenueRule, type RevenueRuleBody, type RevenueRulePreview,
} from '../api/receivables'
import { dateIt, eur, errMsg, todayIso } from '../lib/financeFormat'
import { RuleLifecyclePanel } from './RuleLifecyclePanel'
import {
  Button, Callout, Card, CardContent, CardHeader, DataTable, DataTableEmptyRow, DateField, Dialog, FormGrid, FormSection,
  KeyValueList, LoadingState, Select, StatusPill, TextField, useSnackbar,
} from './ui'

/**
 * Blocco 1 / Card D (REQ-135-001, 135-002, 183-001) — regole di maturazione dei crediti (trigger + base + ritardo d'incasso,
 * per operatore e/o prodotto), versionate: una modifica crea una nuova versione, la precedente si chiude con `valid_to`.
 * Vale la regola piu' specifica (prodotto + operatore > prodotto > operatore). Include le impostazioni di probabilita' e
 * soglie. Anteprima per pratica. Il motivo e' obbligatorio.
 */

interface Props {
  writer: boolean
  /** R2-REGOLE: mostra il ciclo di vita uniforme (solo Admin, solo col flag acceso). */
  lifecycle?: boolean
}

const TRIGGERS = ['Inserita', 'Approvata', 'Attivata', 'Rendicontata'] as const

interface RuleForm {
  operatorId: string
  productId: string
  triggerStatus: string
  basis: RevenueBasis
  fixedAmount: string
  percentage: string
  collectionDelayDays: string
  validFrom: string
  reason: string
}

const blankForm = (): RuleForm => ({
  operatorId: '', productId: '', triggerStatus: 'Attivata', basis: 'company_revenue',
  fixedAmount: '', percentage: '', collectionDelayDays: '60', validFrom: todayIso(), reason: '',
})

const toForm = (r: RevenueRule): RuleForm => ({
  operatorId: r.operatorId === null ? '' : String(r.operatorId),
  productId: r.productId === null ? '' : String(r.productId),
  triggerStatus: r.triggerStatus,
  basis: r.basis,
  fixedAmount: r.fixedAmount === null ? '' : String(r.fixedAmount),
  percentage: r.percentage === null ? '' : String(r.percentage),
  collectionDelayDays: String(r.collectionDelayDays),
  validFrom: todayIso(),
  reason: '',
})

function formError(f: RuleForm): string | null {
  if (!f.reason.trim()) return 'Il motivo è obbligatorio.'
  const delay = Number(f.collectionDelayDays)
  if (f.collectionDelayDays === '' || !Number.isInteger(delay) || delay < 0) return 'Il ritardo d’incasso deve essere un numero intero di giorni.'
  if (f.basis === 'fixed' && !(Number(f.fixedAmount) > 0)) return 'Indica un importo fisso maggiore di zero.'
  if (f.basis === 'pct_expected' && !(Number(f.percentage) > 0 && Number(f.percentage) <= 100)) return 'La percentuale deve essere tra 0 e 100.'
  return null
}

function toBody(f: RuleForm): RevenueRuleBody {
  return {
    operatorId: f.operatorId ? Number(f.operatorId) : null,
    productId: f.productId ? Number(f.productId) : null,
    triggerStatus: f.triggerStatus,
    basis: f.basis,
    fixedAmount: f.basis === 'fixed' ? Number(f.fixedAmount) : null,
    percentage: f.basis === 'pct_expected' ? Number(f.percentage) : null,
    collectionDelayDays: Number(f.collectionDelayDays),
    validFrom: f.validFrom,
    reason: f.reason.trim(),
  }
}

export function RevenueRulesPanel({ writer, lifecycle = false }: Props) {
  const snackbar = useSnackbar()
  const [rules, setRules] = useState<RevenueRule[]>([])
  const [operators, setOperators] = useState<Operator[]>([])
  const [products, setProducts] = useState<{ id: number; name: string }[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [dialog, setDialog] = useState<{ base: RevenueRule | null } | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      setRules(await revenueRulesApi.list())
    } catch (e) {
      setError(errMsg(e, 'Impossibile caricare le regole.'))
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { void load() }, [load])
  useEffect(() => {
    let alive = true
    void fetchOperators().then(r => { if (alive) setOperators(r.items) }).catch(() => undefined)
    void fetchProducts({ page: 1, pageSize: 200 }).then(r => { if (alive) setProducts(r.items.map(p => ({ id: p.id, name: p.name }))) }).catch(() => undefined)
    return () => { alive = false }
  }, [])

  const opName = (id: number | null) => (id === null ? 'Tutti' : operators.find(o => o.id === id)?.name ?? `Operatore ${id}`)
  const prodName = (id: number | null) => (id === null ? 'Tutti' : products.find(p => p.id === id)?.name ?? `Prodotto ${id}`)

  return (
    <>
      <Card variant="outlined" padding="none">
        <CardHeader
          title="Regole di maturazione"
          subtitle="Vale la regola più specifica. Ogni modifica crea una nuova versione: le pratiche già maturate conservano la regola originale."
          actions={writer ? <Button variant="filled" icon="add" onClick={() => setDialog({ base: null })}>Nuova regola</Button> : undefined}
        />
        {error && <CardContent><Callout tone="error">{error}</Callout></CardContent>}
        {loading ? <LoadingState /> : (
          <DataTable variant="plain" aria-label="Regole di maturazione">
            <thead>
              <tr>
                <th>Operatore</th><th>Prodotto</th><th>Quando matura</th><th>Importo</th><th className="text-end">Ritardo</th>
                <th>Validità</th><th className="text-end">Versione</th><th>Motivo</th>{writer && <th><span className="md-sr-only">Azioni</span></th>}
              </tr>
            </thead>
            <tbody>
              {rules.length === 0 && <DataTableEmptyRow colSpan={writer ? 9 : 8} icon="rule" title="Nessuna regola" description="Senza una regola valida le pratiche non maturano ricavi." />}
              {rules.map(r => (
                <tr key={r.id}>
                  <td>{opName(r.operatorId)}</td>
                  <td>{prodName(r.productId)}</td>
                  <td>Pratica «{r.triggerStatus}»</td>
                  <td>
                    {REVENUE_BASIS_LABELS[r.basis]}
                    {r.basis === 'fixed' && r.fixedAmount !== null ? ` · ${eur(r.fixedAmount)}` : ''}
                    {r.basis === 'pct_expected' && r.percentage !== null ? ` · ${r.percentage}%` : ''}
                  </td>
                  <td className="text-end md-num">{r.collectionDelayDays} gg</td>
                  <td>
                    {dateIt(r.validFrom)} – {r.validTo ? dateIt(r.validTo) : <StatusPill tone="success">In vigore</StatusPill>}
                  </td>
                  <td className="text-end md-num">{r.version}</td>
                  <td>{r.reason}</td>
                  {writer && (
                    <td>{r.validTo ? null : <Button variant="text" size="sm" icon="edit" onClick={() => setDialog({ base: r })}>Nuova versione</Button>}</td>
                  )}
                </tr>
              ))}
            </tbody>
          </DataTable>
        )}
      </Card>

      {lifecycle && <RuleLifecyclePanel family="revenue" onChanged={() => void load()} />}

      <PreviewCard />
      <SettingsCard writer={writer} />

      {dialog && (
        <RuleDialog
          base={dialog.base}
          operators={operators}
          products={products}
          onClose={() => setDialog(null)}
          onSaved={() => { setDialog(null); snackbar.show({ message: 'Regola salvata.', tone: 'success' }); void load() }}
        />
      )}
    </>
  )
}

function RuleDialog({
  base, operators, products, onClose, onSaved,
}: { base: RevenueRule | null; operators: Operator[]; products: { id: number; name: string }[]; onClose: () => void; onSaved: () => void }) {
  const snackbar = useSnackbar()
  const [f, setF] = useState<RuleForm>(base ? toForm(base) : blankForm())
  const [busy, setBusy] = useState(false)
  const [touched, setTouched] = useState(false)
  const err = formError(f)
  const set = <K extends keyof RuleForm>(k: K, v: RuleForm[K]) => setF(prev => ({ ...prev, [k]: v }))

  async function submit(e: FormEvent) {
    e.preventDefault()
    setTouched(true)
    if (err) return
    setBusy(true)
    try {
      if (base) await revenueRulesApi.newVersion(base.id, toBody(f))
      else await revenueRulesApi.create(toBody(f))
      onSaved()
    } catch (x) {
      snackbar.show({ message: errMsg(x, 'Salvataggio non riuscito.'), tone: 'error' })
      setBusy(false)
    }
  }

  return (
    <Dialog
      open
      onClose={onClose}
      as="form"
      onSubmit={e => void submit(e)}
      title={base ? `Nuova versione della regola (v${base.version})` : 'Nuova regola di maturazione'}
      supportingText="La versione precedente viene chiusa alla data di inizio indicata."
      actions={
        <>
          <Button variant="text" dismiss onClick={onClose}>Annulla</Button>
          <Button variant="filled" type="submit" loading={busy}>Salva</Button>
        </>
      }
    >
      <FormSection title="Ambito">
        <Select label="Operatore" value={f.operatorId} onChange={e => set('operatorId', e.target.value)}>
          <option value="">Tutti</option>
          {operators.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
        </Select>
        <Select label="Prodotto" value={f.productId} onChange={e => set('productId', e.target.value)}>
          <option value="">Tutti</option>
          {products.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
        </Select>
      </FormSection>
      <FormSection title="Maturazione">
        <Select label="Matura quando la pratica è" value={f.triggerStatus} onChange={e => set('triggerStatus', e.target.value)}>
          {TRIGGERS.map(t => <option key={t} value={t}>{t}</option>)}
        </Select>
        <Select label="Importo" value={f.basis} onChange={e => set('basis', e.target.value as RevenueBasis)}>
          {(Object.keys(REVENUE_BASIS_LABELS) as RevenueBasis[]).map(b => <option key={b} value={b}>{REVENUE_BASIS_LABELS[b]}</option>)}
        </Select>
        {f.basis === 'fixed' && <TextField label="Importo fisso" type="number" min={0} step="0.01" prefix="€" value={f.fixedAmount} onChange={e => set('fixedAmount', e.target.value)} />}
        {f.basis === 'pct_expected' && <TextField label="Percentuale dell'atteso" type="number" min={0} max={100} step="0.01" suffix="%" value={f.percentage} onChange={e => set('percentage', e.target.value)} />}
        <TextField label="Ritardo d'incasso" type="number" min={0} step={1} suffix="giorni" value={f.collectionDelayDays} onChange={e => set('collectionDelayDays', e.target.value)} />
        <DateField kind="date" label="Valida dal" value={f.validFrom} onChange={e => set('validFrom', e.target.value)} />
      </FormSection>
      <FormGrid columns={1}>
        <TextField label="Motivo" required multiline value={f.reason} error={touched && err ? err : undefined} onChange={e => set('reason', e.target.value)} />
      </FormGrid>
    </Dialog>
  )
}

function PreviewCard() {
  const [contractId, setContractId] = useState('')
  const [result, setResult] = useState<RevenueRulePreview | null>(null)
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)

  async function run(e: FormEvent) {
    e.preventDefault()
    const id = Number(contractId)
    if (!Number.isInteger(id) || id <= 0) { setError('Indica il numero di una pratica.'); return }
    setBusy(true)
    setError('')
    try {
      setResult(await revenueRulesApi.preview(id))
    } catch (x) {
      setResult(null)
      setError(errMsg(x, 'Anteprima non disponibile.'))
    } finally {
      setBusy(false)
    }
  }

  const show = (v: unknown): string => {
    if (v === null || v === undefined) return '—'
    if (typeof v === 'number') return Number.isInteger(v) ? String(v) : eur(v)
    if (typeof v === 'string') return /^\d{4}-\d{2}-\d{2}/.test(v) ? dateIt(v) : v
    return JSON.stringify(v)
  }

  return (
    <Card variant="outlined">
      <CardHeader title="Anteprima su una pratica" subtitle="Quale regola varrebbe, con quale importo e in quali date." />
      <CardContent>
        <form onSubmit={e => void run(e)}>
          <FormGrid>
            <TextField label="Numero pratica" type="number" min={1} value={contractId} onChange={e => setContractId(e.target.value)} />
            <div><Button variant="tonal" type="submit" icon="visibility" loading={busy}>Mostra anteprima</Button></div>
          </FormGrid>
        </form>
        {error && <Callout tone="warning">{error}</Callout>}
        {result && <KeyValueList columns={2} items={Object.entries(result).map(([k, v]) => ({ label: k, value: show(v) }))} />}
      </CardContent>
    </Card>
  )
}

function SettingsCard({ writer }: { writer: boolean }) {
  const snackbar = useSnackbar()
  const [settings, setSettings] = useState<ReceivableSettings | null>(null)
  const [probs, setProbs] = useState<Record<string, string>>({})
  const [overdue, setOverdue] = useState('')
  const [prudent, setPrudent] = useState('')
  const [lookback, setLookback] = useState('')
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    let alive = true
    void receivablesApi.settings().then(s => {
      if (!alive) return
      setSettings(s)
      setProbs(Object.fromEntries(Object.entries(s.probabilities).map(([k, v]) => [k, String(v)])))
      setOverdue(String(s.overdueAlertDays))
      setPrudent(String(s.prudentMinProbability))
      setLookback(String(s.realisticLookbackMonths))
    }).catch(e => { if (alive) setError(errMsg(e, 'Impostazioni non disponibili.')) })
    return () => { alive = false }
  }, [])

  const pctOk = (v: string) => v !== '' && Number.isInteger(Number(v)) && Number(v) >= 0 && Number(v) <= 100
  const posOk = (v: string) => v !== '' && Number.isInteger(Number(v)) && Number(v) > 0
  const valid = Object.values(probs).every(pctOk) && pctOk(prudent) && posOk(overdue) && posOk(lookback)

  async function save(e: FormEvent) {
    e.preventDefault()
    if (!valid) return
    setBusy(true)
    try {
      const s = await receivablesApi.saveSettings({
        probabilities: Object.fromEntries(Object.entries(probs).map(([k, v]) => [k, Number(v)])),
        overdueAlertDays: Number(overdue),
        prudentMinProbability: Number(prudent),
        realisticLookbackMonths: Number(lookback),
      })
      setSettings(s)
      snackbar.show({ message: 'Impostazioni salvate.', tone: 'success' })
    } catch (x) {
      snackbar.show({ message: errMsg(x, 'Salvataggio non riuscito.'), tone: 'error' })
    } finally {
      setBusy(false)
    }
  }

  return (
    <Card variant="outlined">
      <CardHeader title="Probabilità e soglie" subtitle="Alimentano gli scenari di liquidità e l'avviso sui crediti scaduti." />
      <CardContent>
        {error && <Callout tone="warning">{error}</Callout>}
        {settings && (
          <form onSubmit={e => void save(e)}>
            <FormSection title="Probabilità di incasso per stato della pratica">
              {Object.keys(probs).map(k => (
                <TextField
                  key={k} label={k} type="number" min={0} max={100} suffix="%" disabled={!writer}
                  value={probs[k] ?? ''} error={pctOk(probs[k] ?? '') ? undefined : 'Da 0 a 100.'}
                  onChange={e => setProbs({ ...probs, [k]: e.target.value })}
                />
              ))}
            </FormSection>
            <FormSection title="Soglie">
              <TextField label="Avviso credito scaduto da" type="number" min={1} suffix="giorni" disabled={!writer} value={overdue} onChange={e => setOverdue(e.target.value)} />
              <TextField label="Scenario prudente: probabilità minima" type="number" min={0} max={100} suffix="%" disabled={!writer} value={prudent} onChange={e => setPrudent(e.target.value)} />
              <TextField label="Scenario realistico: storico" type="number" min={1} suffix="mesi" disabled={!writer} value={lookback} onChange={e => setLookback(e.target.value)} />
            </FormSection>
            {writer && <Button variant="filled" type="submit" loading={busy} disabled={!valid}>Salva impostazioni</Button>}
          </form>
        )}
      </CardContent>
    </Card>
  )
}
